/**
 * Integrations Resource
 *
 * Thin 1:1 mapping to the integrations API. No business logic, no default
 * parameter injection. The SDK passes params as-is.
 */

import type { HttpLayer } from '../http.js';
import type {
  IntegrationDefinition,
  CreateDefinitionRequest,
  UpdateDefinitionRequest,
  DefinitionVersion,
  // The barrel exports automation's `PublishVersionRequest` ({ changelog? })
  // under the bare name and THIS one under the alias. Importing the bare name
  // here silently typed `publishVersion` with the automation shape, so the
  // required `dsl_content` body field (`json:"dsl_content" required:"true"` on
  // the Go handler) was rejected as an unknown property at every call site.
  IntegrationPublishVersionRequest,
  IntegrationConnection,
  CreateConnectionRequest,
  UpdateConnectionRequest,
  UpgradeConnectionRequest,
  AuthenticateAPIKeyRequest,
  AuthenticateClientCredsRequest,
  InitiateOAuth2Request,
  InitiateOAuth2Response,
  TestActionRequest,
  ActionExecutionResponse,
  ExecutionLogEntry,
  ListConnectionsParams,
  GetConnectionLogsParams,
  CatalogSearchResponse,
  CatalogEntry,
  SearchCatalogParams,
  WebhookConfig,
  CreateWebhookConfigRequest,
  WebhookDelivery,
  ListWebhookDeliveriesParams,
  IntegrationMetrics,
} from '../types/index.js';

const prefix = '/v1/integrations';

export class IntegrationsResource {
  constructor(private http: HttpLayer) {}

  // --- Definitions ---

  async createDefinition(
    params: CreateDefinitionRequest,
  ): Promise<IntegrationDefinition> {
    return this.http.post(`${prefix}/definitions`, params);
  }

  async listDefinitions(): Promise<IntegrationDefinition[]> {
    return this.http.get(`${prefix}/definitions`);
  }

  async getDefinition(definitionId: string): Promise<IntegrationDefinition> {
    return this.http.get(`${prefix}/definitions/${definitionId}`);
  }

  async updateDefinition(
    definitionId: string,
    params: UpdateDefinitionRequest,
  ): Promise<IntegrationDefinition> {
    return this.http.put(`${prefix}/definitions/${definitionId}`, params);
  }

  async deleteDefinition(definitionId: string): Promise<IntegrationDefinition> {
    return this.http.delete(`${prefix}/definitions/${definitionId}`);
  }

  // --- Versions ---

  async publishVersion(
    definitionId: string,
    params: IntegrationPublishVersionRequest,
  ): Promise<DefinitionVersion> {
    return this.http.post(
      `${prefix}/definitions/${definitionId}/publish`,
      params,
    );
  }

  async listVersions(definitionId: string): Promise<DefinitionVersion[]> {
    return this.http.get(
      `${prefix}/definitions/${definitionId}/versions`,
    );
  }

  async getVersion(
    definitionId: string,
    versionId: string,
  ): Promise<DefinitionVersion> {
    return this.http.get(
      `${prefix}/definitions/${definitionId}/versions/${versionId}`,
    );
  }

  // --- Connections ---

  async createConnection(
    params: CreateConnectionRequest,
  ): Promise<IntegrationConnection> {
    return this.http.post(`${prefix}/connections`, params);
  }

  async getConnection(connectionId: string): Promise<IntegrationConnection> {
    return this.http.get(`${prefix}/connections/${connectionId}`);
  }

  async listConnections(
    params?: ListConnectionsParams,
  ): Promise<IntegrationConnection[]> {
    return this.http.get(`${prefix}/connections`, params);
  }

  async updateConnection(
    connectionId: string,
    params: UpdateConnectionRequest,
  ): Promise<IntegrationConnection> {
    return this.http.put(`${prefix}/connections/${connectionId}`, params);
  }

  async deleteConnection(connectionId: string): Promise<IntegrationConnection> {
    return this.http.delete(`${prefix}/connections/${connectionId}`);
  }

  async disableConnection(connectionId: string): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/disable`,
    );
  }

  async enableConnection(connectionId: string): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/enable`,
    );
  }

  async reauthenticateConnection(
    connectionId: string,
  ): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/reauthenticate`,
    );
  }

  async upgradeConnection(
    connectionId: string,
    params: UpgradeConnectionRequest,
  ): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/upgrade`,
      params,
    );
  }

  // --- Authentication ---

  async authenticateAPIKey(
    connectionId: string,
    params: AuthenticateAPIKeyRequest,
  ): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/authenticate/api-key`,
      params,
    );
  }

  async authenticateClientCredentials(
    connectionId: string,
    params: AuthenticateClientCredsRequest,
  ): Promise<IntegrationConnection> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/authenticate/client-credentials`,
      params,
    );
  }

  async initiateOAuth2(
    connectionId: string,
    params: InitiateOAuth2Request,
  ): Promise<InitiateOAuth2Response> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/authenticate/oauth2`,
      params,
    );
  }

  // --- Test Action ---

  async testAction(
    connectionId: string,
    params: TestActionRequest,
  ): Promise<ActionExecutionResponse> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/test`,
      params,
    );
  }

  // --- Execution Logs & Health ---

  async getConnectionLogs(
    connectionId: string,
    params?: GetConnectionLogsParams,
  ): Promise<ExecutionLogEntry[]> {
    return this.http.get(
      `${prefix}/connections/${connectionId}/logs`,
      params,
    );
  }

  async getConnectionHealth(
    connectionId: string,
  ): Promise<IntegrationConnection> {
    return this.http.get(
      `${prefix}/connections/${connectionId}/health`,
    );
  }

  // --- Catalog ---

  async searchCatalog(
    params?: SearchCatalogParams,
  ): Promise<CatalogSearchResponse> {
    return this.http.get(`${prefix}/catalog`, params);
  }

  async getCatalogEntry(slug: string): Promise<CatalogEntry> {
    return this.http.get(`${prefix}/catalog/${slug}`);
  }

  // --- Webhooks ---

  async createWebhookConfig(
    connectionId: string,
    params: CreateWebhookConfigRequest,
  ): Promise<WebhookConfig> {
    return this.http.post(
      `${prefix}/connections/${connectionId}/webhooks`,
      params,
    );
  }

  async getWebhookConfig(connectionId: string): Promise<WebhookConfig> {
    return this.http.get(
      `${prefix}/connections/${connectionId}/webhooks`,
    );
  }

  async deleteWebhookConfig(
    connectionId: string,
  ): Promise<{ deleted: boolean }> {
    return this.http.delete(
      `${prefix}/connections/${connectionId}/webhooks`,
    );
  }

  async listWebhookDeliveries(
    connectionId: string,
    params?: ListWebhookDeliveriesParams,
  ): Promise<WebhookDelivery[]> {
    return this.http.get(
      `${prefix}/connections/${connectionId}/webhooks/deliveries`,
      params,
    );
  }

  // --- Admin ---

  private adminPrefix = '/v1/admin/integrations';

  async adminStartHealthScheduler(): Promise<unknown> {
    return this.http.post(
      `${this.adminPrefix}/health-check-scheduler/start`,
    );
  }

  async adminMetrics(): Promise<IntegrationMetrics> {
    return this.http.get(`${this.adminPrefix}/metrics`);
  }
}
